'use client';

import type { ReactElement } from 'react';
import { Fragment, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Controller,
  useController,
  useWatch,
  type Control
} from 'react-hook-form';
import { formatDateAU } from '@/lib/formatters/australian-formatters';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';
import {
  LEGAL_PERSONAL_REPRESENTATIVE_ROLES,
  RELATIONSHIP_OPTIONS,
  RELATIONSHIP_LABELS,
  NON_PROVING_REASONS,
  NON_PROVING_REASON_LABELS,
  isDeathNonProvingReason
} from '@/lib/constants';
import type { Contact } from '@/lib/services/contacts/types';
import type { ContactFormSectionProps } from './types';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';
import { PmsContactBindingRow } from './PmsContactBindingRow';
import { PmsContactTypeSelector } from './PmsContactTypeSelector';

/**
 * "Status" control for an LPR: a "This executor is not proving / not acting"
 * checkbox that, once ticked, reveals a reason dropdown (Leave reserved / Renunciation /
 * Predeceased / Died after the will-maker / Incapacity / Passed over), each shown
 * with explanatory subtext
 * when the list is open. Executors are proving in the overwhelming majority of
 * cases, so the common path is a single unticked checkbox and the reason picker
 * only appears for the exception — matching the progressive-disclosure idiom
 * used by the deceased / minor controls in this form.
 * Replaces the old single "Non-proving (leave reserved)" checkbox. See LF-5372.
 *
 * Executor-only: proving vs non-proving (and "power reserved / leave reserved")
 * applies to executors of a will, not to an applicant for letters of
 * administration (who has no power to reserve). The control is therefore hidden
 * unless the LPR role is "executor", and if the role is changed away from
 * executor any non-proving state is reset back to proving so an LPR can't stay
 * silently non-proving through a hidden control.
 *
 * `isProvingLpr` and `nonProvingReason` are kept consistent: unticking clears
 * the reason; ticking marks the LPR non-proving and defaults the reason to
 * leave reserved. Choosing either reason that means the executor has died
 * ("Predeceased", "Died after the will-maker") also flags the contact deceased,
 * avoiding a contradictory state; the lawyer can still untick it in the
 * Deceased section below.
 */
function LprProvingStatusField({
  control
}: {
  control: Control<Partial<Contact>>;
}): ReactElement | null {
  const { field: provingField } = useController({
    control,
    name: 'isProvingLpr'
  });
  const { field: reasonField } = useController({
    control,
    name: 'nonProvingReason'
  });
  const { field: deceasedField } = useController({
    control,
    name: 'isDeceased'
  });
  const role = useWatch({ control, name: 'legalPersonalRepresentativeRole' });
  const isExecutor = role === 'executor';

  // Non-proving is executor-only. If the role is (or becomes) something else,
  // reset any non-proving state so it can't persist behind the hidden control.
  useEffect(() => {
    if (!isExecutor && provingField.value === false) {
      provingField.onChange(true);
      reasonField.onChange(null);
    }
  }, [isExecutor, provingField, reasonField]);

  if (!isExecutor) return null;

  const isNonProving = provingField.value === false;
  const selectedReason = reasonField.value ?? 'leave_reserved';

  const handleToggle = (checked: boolean): void => {
    if (checked) {
      provingField.onChange(false);
      reasonField.onChange(selectedReason);
    } else {
      provingField.onChange(true);
      reasonField.onChange(null);
    }
  };

  const handleReasonChange = (value: string): void => {
    reasonField.onChange(value);
    if (isDeathNonProvingReason(value)) {
      deceasedField.onChange(true);
    }
  };

  return (
    <div className={tokens.patterns.form.spacing}>
      <div className="flex items-center gap-2">
        <Checkbox
          id="isProvingLpr"
          checked={isNonProving}
          onCheckedChange={(checked) => handleToggle(checked === true)}
        />
        <label
          htmlFor="isProvingLpr"
          className={cn(tokens.patterns.form.label, 'cursor-pointer')}
        >
          This executor is not proving / not acting
        </label>
      </div>

      {isNonProving && (
        <div className={tokens.patterns.form.spacing}>
          <label
            htmlFor="non-proving-reason"
            className={tokens.patterns.form.label}
          >
            Reason
          </label>
          <Select value={selectedReason} onValueChange={handleReasonChange}>
            <SelectTrigger id="non-proving-reason">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NON_PROVING_REASONS.map((reason) => (
                <SelectItem
                  key={reason.value}
                  value={reason.value}
                  description={reason.description}
                >
                  {reason.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">
            Non-proving executors are excluded from generated forms,
            correspondence, and distribution-advice signing, but stay visible as
            an executor of record. At least one LPR on the matter must remain
            proving.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * LPR-conditional fields broken out so `useWatch` re-renders only this subtree,
 * not the entire RoleSection on every other field's keystroke.
 */
function LprConditionalFields({
  control
}: {
  control: Control<Partial<Contact>>;
}): ReactElement | null {
  const isLPR = useWatch({ control, name: 'isLegalPersonalRepresentative' });
  const { today, minDate } = useMemo(() => {
    const now = new Date();
    return {
      today: now,
      minDate: new Date(now.getFullYear() - 120, now.getMonth(), now.getDate())
    };
  }, []);
  if (!isLPR) return null;

  return (
    <Fragment>
      <Controller
        control={control}
        name="legalPersonalRepresentativeRole"
        render={({ field, fieldState }) => (
          <FormField
            label="Role"
            value={(field.value as string) || ''}
            mode="edit"
            type="select"
            options={LEGAL_PERSONAL_REPRESENTATIVE_ROLES}
            placeholder="Select role..."
            onChange={(val) => field.onChange(val || null)}
            error={fieldState.error?.message}
            clearable
          />
        )}
      />

      <LprProvingStatusField control={control} />

      <Controller
        control={control}
        name="dateOfBirth"
        render={({ field, fieldState }) => {
          const dateValue = field.value
            ? new Date(field.value as string)
            : undefined;
          return (
            <div className={tokens.patterns.form.spacing}>
              <label
                htmlFor="dateOfBirth"
                className={tokens.patterns.form.label}
              >
                Date of Birth
              </label>
              <DatePicker
                date={dateValue}
                onDateChange={(date) => {
                  field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
                }}
                placeholder="DD/MM/YYYY"
                yearNavigation
                minDate={minDate}
                maxDate={today}
                allowInput
                className={cn(
                  tokens.patterns.form.input,
                  fieldState.error && 'border-destructive'
                )}
              />
              {fieldState.error?.message && (
                <span className="text-destructive text-sm">
                  {fieldState.error.message}
                </span>
              )}
            </div>
          );
        }}
      />
    </Fragment>
  );
}

/**
 * Beneficiary-conditional checkbox (`isMinor`), broken out for the same reason
 * as `LprConditionalFields`.
 */
function BeneficiaryConditionalFields({
  control
}: {
  control: Control<Partial<Contact>>;
}): ReactElement | null {
  const isBeneficiary = useWatch({ control, name: 'isBeneficiary' });
  if (!isBeneficiary) return null;

  return (
    <Controller
      control={control}
      name="isMinor"
      render={({ field, fieldState }) => (
        <div className="flex items-center gap-2">
          <Checkbox
            id="isMinor"
            checked={Boolean(field.value)}
            onCheckedChange={(checked) => field.onChange(checked)}
          />
          <label
            htmlFor="isMinor"
            className={cn(tokens.patterns.form.label, 'cursor-pointer')}
          >
            This contact is a minor
          </label>
          {fieldState.error?.message && (
            <span className="text-destructive text-sm">
              {fieldState.error.message}
            </span>
          )}
        </div>
      )}
    />
  );
}

/**
 * Date-of-death edit field. Only rendered once the contact is flagged deceased
 * (see `DeceasedFields`) — it is optional supporting detail, not the primary
 * signal. Constrained to: past dates only, not earlier than dateOfBirth.
 */
function DateOfDeathField({
  control
}: {
  control: Control<Partial<Contact>>;
}): ReactElement {
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });
  const { today, minDate } = useMemo(() => {
    const now = new Date();
    const dobMin = dateOfBirth ? new Date(dateOfBirth as string) : undefined;
    return {
      today: now,
      minDate:
        dobMin && !Number.isNaN(dobMin.getTime())
          ? dobMin
          : new Date(now.getFullYear() - 150, now.getMonth(), now.getDate())
    };
  }, [dateOfBirth]);

  return (
    <Controller
      control={control}
      name="dateOfDeath"
      render={({ field, fieldState }) => {
        const dateValue = field.value
          ? new Date(field.value as string)
          : undefined;
        return (
          <div className={tokens.patterns.form.spacing}>
            <label htmlFor="dateOfDeath" className={tokens.patterns.form.label}>
              Date of Death
            </label>
            <DatePicker
              date={dateValue}
              onDateChange={(date) => {
                field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
              }}
              placeholder="DD/MM/YYYY"
              yearNavigation
              minDate={minDate}
              maxDate={today}
              allowInput
              className={cn(
                tokens.patterns.form.input,
                fieldState.error && 'border-destructive'
              )}
            />
            <span className="text-muted-foreground text-xs">
              Set if this contact has died, e.g. a beneficiary who predeceased
              the testator.
            </span>
            {fieldState.error?.message && (
              <span className="text-destructive text-sm">
                {fieldState.error.message}
              </span>
            )}
          </div>
        );
      }}
    />
  );
}

/**
 * "This contact is deceased" checkbox plus progressive disclosure of the
 * optional Date of Death field. The boolean is the primary signal (a deceased
 * contact — e.g. a predeceased beneficiary); the date only appears once the box
 * is ticked, keeping it off the happy path. Broken out so the `useWatch`
 * re-renders only this subtree.
 */
function DeceasedFields({
  control
}: {
  control: Control<Partial<Contact>>;
}): ReactElement {
  const isDeceased = useWatch({ control, name: 'isDeceased' });

  return (
    <Fragment>
      <Controller
        control={control}
        name="isDeceased"
        render={({ field, fieldState }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="isDeceased"
              checked={Boolean(field.value)}
              onCheckedChange={(checked) => field.onChange(checked)}
            />
            <label
              htmlFor="isDeceased"
              className={cn(tokens.patterns.form.label, 'cursor-pointer')}
            >
              This contact is deceased
            </label>
            {fieldState.error?.message && (
              <span className="text-destructive text-sm">
                {fieldState.error.message}
              </span>
            )}
          </div>
        )}
      />
      {Boolean(isDeceased) && <DateOfDeathField control={control} />}
    </Fragment>
  );
}

/**
 * The role controls themselves. Split from the exported `RoleSection` below only so the PMS binding
 * row can sit alongside them once, rather than being repeated across this component's four separate
 * return paths (estate/non-estate × read/edit).
 */
function RoleSectionFields({
  control,
  mode,
  values,
  clickToCopy = false,
  isEstate = true
}: ContactFormSectionProps): ReactElement {
  const fieldChanges = useFieldChanges();

  // Non-estate matters: the meaningful contact role is "client"
  // (ContactMatter.isClient). Render a single client-only Role select — the
  // estate LPR / Beneficiary / deceased controls don't apply here (LF-5788).
  if (!isEstate) {
    if (mode === 'read') {
      return (
        <Fragment>
          {values?.isClient && (
            <FormField
              label="Role"
              value="Client"
              mode="read"
              clickToCopy={clickToCopy}
            />
          )}
        </Fragment>
      );
    }

    return (
      <Controller
        control={control as Control<Partial<Contact>>}
        name="isClient"
        render={({ field, fieldState }) => (
          <FormField
            label="Role"
            value={field.value ? 'client' : ''}
            mode="edit"
            type="select"
            options={[{ value: 'client', label: 'Client' }]}
            placeholder="Select role..."
            onChange={(val) => field.onChange(val === 'client')}
            error={fieldState.error?.message}
            clearable
          />
        )}
      />
    );
  }

  if (mode === 'read') {
    const dobChange = fieldChanges?.dateOfBirth;
    const dodChange = fieldChanges?.dateOfDeath;
    // Surface an AI-proposed LPR-role fill (e.g. an existing executor whose
    // role string was blank) as an enrichment, so an `update` card isn't shown
    // with the "Updated by Vale AI" badge but no visibly-flagged field.
    const lprRoleChange = fieldChanges?.legalPersonalRepresentativeRole;

    return (
      <Fragment>
        {values?.isLegalPersonalRepresentative &&
          values?.legalPersonalRepresentativeRole &&
          lprRoleChange?.changeType !== 'conflict' && (
            <FormField
              label="LPR Role"
              value={
                LEGAL_PERSONAL_REPRESENTATIVE_ROLES.find(
                  (r) => r.value === values.legalPersonalRepresentativeRole
                )?.label || values.legalPersonalRepresentativeRole
              }
              mode="read"
              clickToCopy={clickToCopy}
              changeType={
                lprRoleChange?.changeType === 'enrichment'
                  ? 'enrichment'
                  : undefined
              }
            />
          )}

        {values?.isLegalPersonalRepresentative &&
          values?.isProvingLpr === false && (
            <FormField
              label="Status"
              value={`Non-proving (${
                NON_PROVING_REASON_LABELS[
                  values.nonProvingReason ?? 'leave_reserved'
                ]
              })`}
              mode="read"
              clickToCopy={clickToCopy}
            />
          )}

        {values?.isLegalPersonalRepresentative &&
          values?.dateOfBirth &&
          dobChange?.changeType !== 'conflict' && (
            <FormField
              label="Date of Birth"
              value={formatDateAU(values.dateOfBirth)}
              mode="read"
              clickToCopy={clickToCopy}
              changeType={
                dobChange?.changeType === 'enrichment'
                  ? 'enrichment'
                  : undefined
              }
            />
          )}

        {values?.isBeneficiary && values?.isMinor && (
          <FormField
            label="This contact is a minor"
            value="Yes"
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {values?.relationshipToDeceased && (
          <FormField
            label="Relationship to deceased"
            value={
              RELATIONSHIP_LABELS[values.relationshipToDeceased] ||
              values.relationshipToDeceased
            }
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {(values?.isDeceased || values?.dateOfDeath) &&
          dodChange?.changeType !== 'conflict' && (
            <FormField
              label={values?.dateOfDeath ? 'Date of Death' : 'Deceased'}
              value={
                values?.dateOfDeath ? formatDateAU(values.dateOfDeath) : 'Yes'
              }
              mode="read"
              clickToCopy={clickToCopy}
              changeType={
                dodChange?.changeType === 'enrichment'
                  ? 'enrichment'
                  : undefined
              }
            />
          )}
      </Fragment>
    );
  }

  // Edit mode
  return (
    <Fragment>
      <div className="flex items-center gap-4">
        <Controller
          control={control as Control<Partial<Contact>>}
          name="isLegalPersonalRepresentative"
          render={({ field, fieldState }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="isLegalPersonalRepresentative"
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => field.onChange(checked)}
              />
              <label
                htmlFor="isLegalPersonalRepresentative"
                className={cn(tokens.patterns.form.label, 'cursor-pointer')}
              >
                Legal Personal Representative
              </label>
              {fieldState.error?.message && (
                <span className="text-destructive text-sm">
                  {fieldState.error.message}
                </span>
              )}
            </div>
          )}
        />

        <Controller
          control={control as Control<Partial<Contact>>}
          name="isBeneficiary"
          render={({ field, fieldState }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="isBeneficiary"
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => field.onChange(checked)}
              />
              <label
                htmlFor="isBeneficiary"
                className={cn(tokens.patterns.form.label, 'cursor-pointer')}
              >
                Beneficiary
              </label>
              {fieldState.error?.message && (
                <span className="text-destructive text-sm">
                  {fieldState.error.message}
                </span>
              )}
            </div>
          )}
        />
      </div>

      <Controller
        control={control as Control<Partial<Contact>>}
        name="relationshipToDeceased"
        render={({ field, fieldState }) => (
          <FormField
            label="Relationship to deceased"
            value={(field.value as string) || ''}
            mode="edit"
            type="select"
            options={RELATIONSHIP_OPTIONS}
            placeholder="Select relationship..."
            onChange={(val) => field.onChange(val || null)}
            error={fieldState.error?.message}
            clearable
          />
        )}
      />

      <LprConditionalFields control={control as Control<Partial<Contact>>} />
      <BeneficiaryConditionalFields
        control={control as Control<Partial<Contact>>}
      />
      <DeceasedFields control={control as Control<Partial<Contact>>} />
    </Fragment>
  );
}

/**
 * Contact role controls, plus — when this contact is being edited **on a matter** — its PMS binding
 * and the explicit unbind action.
 *
 * The binding belongs here rather than in its own section because it is a per-`(contact, matter)`
 * role fact, exactly like the LPR / client controls above it: which type a contact is filed under in
 * the firm's PMS is the same kind of statement as whether they are the client.
 */
export function RoleSection(props: ContactFormSectionProps): ReactElement {
  const { values, matterId } = props;

  const contactId = values?.id;

  return (
    <Fragment>
      <RoleSectionFields {...props} />
      {/*
       * Choosing the type, and the binding it produces, sit together — the picker is the control and
       * the row below is its outcome. Both are per-`(contact, matter)` role facts, which is why they
       * live here beside the LPR / client controls rather than in a section of their own.
       *
       * The picker is deliberately NOT gated on `matterId`: its own gate is firm-level (does the
       * firm's active PMS supply a catalogue?), and it must render during **matter creation**, where
       * no matter exists yet. It renders nothing in read mode, since the row already shows the
       * stored type there.
       */}
      <PmsContactTypeSelector
        control={props.control}
        mode={props.mode}
        values={values ?? {}}
        matterId={matterId}
      />
      {/*
       * Gated here, not inside the row: mounting it costs a PMS status query, and the surfaces that
       * reuse this form with no matter behind them — the global contacts list, and the AI-review
       * cards staging *proposed* contacts — must not pay it (nor need a QueryClient to render).
       */}
      {!!matterId && !!contactId && (
        <PmsContactBindingRow
          matterId={matterId}
          contactId={contactId}
          contactName={[values?.firstName, values?.lastName]
            .filter(Boolean)
            .join(' ')}
        />
      )}
    </Fragment>
  );
}
